/* Sundays in Bed — shared cost-estimator engine.
   Single source of truth for pricing constants and maths.
   Used by: /pages/cost-calculator (page body), the Style Quiz, and the quote-email worker.
   Behaviour must match the original inline calculator exactly — change values here only. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SIBEstimator = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Room model — stable IDs. FOC rule (owner, 2026-09-26): up to TWO of utility/yard + foyer are
     free when the project has >=1 bedroom AND a living-dining space, open-plan or separate.

     'kit' (Dry kitchen) and 'wetkit' (Wet kitchen) are two separate, separately-charged picks.
     Many KL condos treat the wet kitchen and the yard as one space; the visitor self-selects
     which it is for them. Calling it a Wet kitchen charges it; calling it Utility / yard keeps
     it inside the FOC rule. That is deliberate, not a loophole — the two describe genuinely
     different scopes of work. */
  var ROOMS = { bed: 'Bedroom', liv: 'Living room', din: 'Dining room', kit: 'Dry kitchen', wetkit: 'Wet kitchen', bath: 'Bathroom', off: 'Home office', bal: 'Balcony', opn: 'Open-plan (living–dining ± kitchen)', util: 'Utility / yard', foyer: 'Foyer', oth: 'Others' };

  /* Design fees confirmed by owner 2026-08-08: standard rooms / open-plan split. */
  /* v = Virtual Design, owner ruling 2026-09-20: USD 279 (RM1,143) per room; an open-plan
     living + dining + kitchen counts as ONE room at USD 349 (RM1,430). Ringgit is the billing
     currency for homes in Malaysia; clients elsewhere pay the USD figure, which this engine
     does not yet render. Before 2026-09-20 'virtual' was routed to the in-person design rate,
     quoting RM3,565 a room against a published RM1,143 - a 3.1x overquote on every remote lead. */
  /* `foc` = does this service include utility/yard + foyer free under the qualifying rule below.
     Interior design and Virtual design do; Interior styling does NOT (owner, 2026-09-20) — its
     published rate card charges both at the standard per-room fee like any other room. Keeping
     this on RATE rather than in an `if` means adding a service can never silently inherit the
     perk, which is exactly how styling came to quote them free. */
  var RATE = {
    s: { std: 1715, opn: 2635, foc: false },
    d: { std: 3565, opn: 5485, foc: true  },
    v: { std: 1143, opn: 1430, foc: true, usd: { std: 279, opn: 349 } }
  };
  /* RATE.v.usd is Virtual Design's published US-dollar card (owner, 2026-09-20), priced in its
     own right, not converted: 1,143 / 279 and 1,430 / 349 are not the same ratio, so converting a
     ringgit total never lands on the card. Until 2026-09-26 the quiz report divided the ringgit
     fee by an exchange rate and rounded to 10, showing "about USD 280" against a USD 279 room. */

  /* Free rooms are capped at two, whatever mix of utility/yard and foyer is picked (owner,
     2026-09-26). Before that every utility and foyer went free, so a project that ticked two of
     each got four rooms for nothing. Both room types cost the same within a service, so which two
     are the free ones never changes the fee. */
  var FOC_MAX = 2;

  /* Interior Design + Interior Styling together (owner, 2026-09-26): every room carries the design
     fee, plus styling at 50% of the normal styling rate — RM857.50 a room, RM1,317.50 open plan.
     Styling has no free rooms of its own (see RATE.s.foc), so the half-price styling applies to
     EVERY room, utility/yard and foyer included, even when the design half of those is free.
     Before 2026-09-26 the quiz priced this pair at the design rate alone and left styling out. */
  var STYLING_WITH_DESIGN = 0.5;

  /* Finish-level multipliers on the design fee (owner 2026-08-08): Essential x1, Premium x1.2, Signature x1.3. */
  var MULT = { e: 1, p: 1.2, s: 1.3 };

  /* Contractor-works bands. [low,high]; paint/floor/ffe are per sq ft, others lump sums.
     TODO OWNER (F09): works bands pending — placeholders, not confirmed. */

  /* ==========================================================================
     SUPERSEDED — DO NOT RENDER ANY OF THE WORKS FIGURES BELOW.

     Everything from WORKS down to totalHi is a rough seven-line renovation
     model: paint/floor/ffe multiplied by floor area, kitchen/wardrobe/electrical/
     plumbing as flat lump sums, three tiers, low and high bound. It knows nothing
     about which rooms you picked, the site condition, the layout, or the wet/dry
     kitchen split. It predates sib-boq-engine.js and it is roughly HALF of it.

     Measured 2026-09-04, same 900 sq ft condo, Premium, full scope:
         this file          RM71k – 142k
         sib-boq-engine.js  RM172k – 297k        <- correct
         /pages/kl-renovation-cost publishes RM120–318/sq ft = RM108k–286k,
         which agrees with the BOQ engine, not with this.

     Nothing renders these today. The cost-calculator section calls estimate()
     with works:[] and displays the design fee only, and says so on the page.
     worker.js reuses the field NAMES worksLo/worksHi but fills them from the BOQ
     engine, so the emailed estimate is correct.

     If you need a contractor-works number, use sib-boq-engine.js. If you wire
     E.WORKS into anything user-facing you will quote roughly half the real cost.

     Scheduled for removal as its own change - it cannot simply be deleted
     because six test files depend on these fields, including
     estimator-parity.test.js and full-integration-check.py, which guard the
     lead pipeline. Remove it with the suite green either side, not in passing.

     The design fee above this comment is correct and is the live half.
     ========================================================================== */
  var WORKS = {
    e: { name: 'Essential', paint: [4, 7],  floor: [9, 16],  kit: [8000, 15000],  ward: [6000, 12000],  elec: [3000, 6000],   plumb: [2000, 5000],  ffe: [8, 15] },
    p: { name: 'Premium',   paint: [7, 12], floor: [16, 32], kit: [15000, 30000], ward: [12000, 24000], elec: [6000, 12000],  plumb: [4000, 9000],  ffe: [15, 30] },
    s: { name: 'Signature', paint: [12, 22], floor: [32, 65], kit: [30000, 60000], ward: [24000, 45000], elec: [12000, 25000], plumb: [8000, 18000], ffe: [30, 60] }
  };
  var PER_SQFT_WORKS = { paint: true, floor: true, ffe: true };

  var LIMITS = { minSqft: 450, maxSqft: 10000, minRoomCount: 0, maxRoomCount: 9 };

  var DEFAULTS = {
    counts: { bed: 2, liv: 1, din: 1, kit: 0, wetkit: 0, bath: 0, off: 0, bal: 0, opn: 0, util: 0, foyer: 0, oth: 0 },
    svc: 'd',
    tier: 'p',
    sqft: 1000,
    works: ['paint', 'kit', 'ward']
  };

  function r100(n) { return Math.round(n / 100) * 100; }
  function clampSqft(sq) {
    sq = parseInt(sq, 10);
    if (isNaN(sq)) sq = DEFAULTS.sqft;
    return Math.min(LIMITS.maxSqft, Math.max(LIMITS.minSqft, sq));
  }
  function normCounts(counts) {
    var out = {};
    for (var k in ROOMS) {
      var v = counts && counts[k] != null ? parseInt(counts[k], 10) : 0;
      if (isNaN(v)) v = 0;
      out[k] = Math.max(LIMITS.minRoomCount, Math.min(LIMITS.maxRoomCount, v));
    }
    return out;
  }

  /* state: {counts:{bed..}, svc:'s'|'d'|'v', tier:'e'|'p'|'s', sqft:Number, works:['paint',...],
            addStyling:Boolean — Interior Styling chosen alongside Interior Design; ignored unless svc is 'd'} */
  function estimate(state) {
    state = state || {};
    var counts = normCounts(state.counts || DEFAULTS.counts);
    /* Derived from RATE, not a hardcoded whitelist: the old list was ('s'|'d') and silently
       fell back to 'd' - the MOST expensive rate - for anything else. That is how virtual leads
       were quoted at the in-person rate for months without erroring. Adding a key to RATE is now
       enough to make it selectable. */
    var svc = RATE[state.svc] ? state.svc : DEFAULTS.svc;
    var tier = WORKS[state.tier] ? state.tier : DEFAULTS.tier;
    var sq = clampSqft(state.sqft != null ? state.sqft : DEFAULTS.sqft);
    var works = Array.isArray(state.works) ? state.works : DEFAULTS.works.slice();

    var roomsN = 0, k;
    for (k in counts) roomsN += counts[k];

    var rate = RATE[svc];

    /* FOC: utility/yard and foyer go free, up to FOC_MAX of them, when the service carries the
       perk AND the project has >=1 bedroom plus its living-dining space, either as an open-plan
       room OR as a separate living room and dining room.
       History: two separate room picks since 2026-08-17. The service test was missing until
       2026-09-20, so Interior styling quoted utility/yard and foyer at zero against a published
       RM1,715 each. Until 2026-09-26 only an open-plan space qualified, which shut out every home
       with separate living and dining rooms (most terraces and semi-Ds); the owner's rule is a
       bedroom and an open space, or a full-home project, and separate living + dining is how a
       full home shows up in the room picks. The cap of two arrived the same day. */
    var hasLivingDining = counts.opn >= 1 || (counts.liv >= 1 && counts.din >= 1);
    var focActive = rate.foc === true && counts.bed >= 1 && hasLivingDining;
    var freeN = focActive ? Math.min(FOC_MAX, counts.util + counts.foyer) : 0;
    var chargeN = roomsN - freeN;

    /* Design fee is flat per room and never scales with finish tier — tier (Essential/
       Premium/Signature) only affects the contractor-works estimate below, never the fee
       itself. This matches the site's own "one flat fee per room" positioning; fixed 2026-08-17
       after finish tier was found silently multiplying the design fee too. */
    var rStd = rate.std, rOpn = rate.opn;
    var opnN = counts.opn, stdN = chargeN - opnN;
    var designFee = stdN * rStd + opnN * rOpn;

    /* Half-price styling on top of the design fee, on every room (free ones included). */
    var addStyling = svc === 'd' && state.addStyling === true;
    var sStd = addStyling ? RATE.s.std * STYLING_WITH_DESIGN : 0;
    var sOpn = addStyling ? RATE.s.opn * STYLING_WITH_DESIGN : 0;
    var stylingFee = addStyling ? (roomsN - opnN) * sStd + opnN * sOpn : 0;
    var fee = designFee + stylingFee;
    /* Same free rooms, same counts, priced off the USD card. null for services billed only in ringgit. */
    var feeUSD = rate.usd ? stdN * rate.usd.std + opnN * rate.usd.opn : null;

    var t = WORKS[tier], lo = 0, hi = 0, any = false;
    for (var i = 0; i < works.length; i++) {
      var w = works[i];
      if (!t[w] || w === 'name') continue;
      any = true;
      if (PER_SQFT_WORKS[w]) { lo += t[w][0] * sq; hi += t[w][1] * sq; }
      else { lo += t[w][0]; hi += t[w][1]; }
    }
    lo = r100(lo); hi = r100(hi);

    return {
      roomsN: roomsN, chargeN: chargeN, freeN: freeN, stdN: stdN, opnN: opnN,
      focActive: focActive, rStd: rStd, rOpn: rOpn, fee: fee,
      designFee: designFee, stylingFee: stylingFee, addStyling: addStyling, sStd: sStd, sOpn: sOpn,
      feeUSD: feeUSD, uStd: rate.usd ? rate.usd.std : null, uOpn: rate.usd ? rate.usd.opn : null,
      anyWorks: any, worksLo: lo, worksHi: hi,
      totalLo: any ? r100(fee + lo) : fee,
      totalHi: any ? r100(fee + hi) : fee,
      tierName: t.name, svc: svc, tier: tier, sqft: sq,
      counts: counts, works: works.slice()
    };
  }

  /* Quiz size bands — stable keys. Email ranges run the engine at band bounds (clamped). */
  var SIZE_BANDS = {
    b1: { label: '1–1,000 sq ft',          lo: 1,    hi: 1000 },
    b2: { label: '1,001–2,000 sq ft',      lo: 1001, hi: 2000 },
    b3: { label: '2,001–3,000 sq ft',      lo: 2001, hi: 3000 },
    b4: { label: 'More than 3,001 sq ft',  lo: 3001, hi: LIMITS.maxSqft }
  };

  function bandBounds(key) {
    var b = SIZE_BANDS[key];
    if (!b) b = SIZE_BANDS.b2;
    return { lo: clampSqft(b.lo), hi: clampSqft(b.hi) };
  }
  function bandRepresentative(key) {
    var b = bandBounds(key);
    var mid = (b.lo + b.hi) / 2;
    return clampSqft(Math.round(mid / 50) * 50);
  }
  /* Range across a size band for a given state (fee is size-invariant; works scale). */
  function estimateBand(state, bandKey) {
    var b = bandBounds(bandKey);
    var loState = {}, hiState = {}, k;
    for (k in state) { loState[k] = state[k]; hiState[k] = state[k]; }
    loState.sqft = b.lo; hiState.sqft = b.hi;
    var eLo = estimate(loState), eHi = estimate(hiState);
    return { lo: eLo, hi: eHi, bounds: b };
  }

  /* Whole ringgit print as before ("RM 16,180"). Half-price styling can leave 50 sen
     (RM857.50 a room), and those print with both decimals ("RM 17,037.50"), never "17,037.5". */
  function fmtUSD(n) { return 'USD ' + Number(n).toLocaleString('en-US'); }
  function fmtRM(n) {
    n = Number(n);
    var sen = Math.round(n * 100) % 100 !== 0;
    return 'RM ' + (sen ? n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n.toLocaleString('en-MY'));
  }

  return {
    ROOMS: ROOMS, RATE: RATE, FOC_MAX: FOC_MAX, STYLING_WITH_DESIGN: STYLING_WITH_DESIGN,
    MULT: MULT, WORKS: WORKS, LIMITS: LIMITS, DEFAULTS: DEFAULTS,
    SIZE_BANDS: SIZE_BANDS,
    estimate: estimate, estimateBand: estimateBand,
    bandBounds: bandBounds, bandRepresentative: bandRepresentative,
    clampSqft: clampSqft, r100: r100, fmtRM: fmtRM, fmtUSD: fmtUSD
  };
}));

